/* Modal de las Microencuestas (nuevos ingresos y Clima Laboral).
 *
 * Comportamiento (espejo del EventoEvaluacionModal):
 * - Al iniciar sesión consulta /microencuesta/pendiente. El backend decide la
 *   elegibilidad y devuelve una sola encuesta por vez (primero la de nuevos
 *   ingresos; si no hay, la de Clima Laboral cuando le toca su ciclo).
 * - "Más tarde":
 *     - postergacion.maximo = null (nuevos ingresos): el modal se cierra y
 *       vuelve a aparecer en el próximo inicio de sesión.
 *     - postergacion.maximo con número (Clima Laboral): se registra en el
 *       servidor; mientras queden postergaciones se puede posponer, después
 *       el botón desaparece y la encuesta es obligatoria.
 * - Tipos de pregunta: LIKERT (1-5), RECOMENDACION (5 opciones con texto, se
 *   guardan como 5..1), OPCION (opción única; "Otro" pide texto), NPS (0-10) y
 *   TEXTO (abierta, opcional si la pregunta no es obligatoria).
 * - El comentario libre de la campaña se muestra solo si la encuesta no trae
 *   su propia pregunta de texto.
 * - También se abre desde la tarjeta del inicio (MicroEncuestaCard, evento
 *   EVENTO_MICROENCUESTA_ABRIR). Abierta así es voluntaria: "Más tarde" solo
 *   cierra, no descuenta postergaciones.
 */
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  useIonToast,
} from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import MicroEncuestaAPI, {
  EVENTO_MICROENCUESTA_ABRIR,
  EVENTO_MICROENCUESTA_RESPONDIDA,
  MicroEncuesta,
  MicroEncuestaPregunta,
  MicroEncuestaRespuesta,
} from "src/services/microencuesta";
import "./MicroEncuestaModal.css";

const OPCIONES = [1, 2, 3, 4, 5] as const;

const OPCIONES_NPS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

const ETIQUETAS_ESCALA: Record<number, string> = {
  1: "Muy en desacuerdo",
  2: "En desacuerdo",
  3: "Ni de acuerdo ni en desacuerdo",
  4: "De acuerdo",
  5: "Muy de acuerdo",
};

// Pregunta tipo RECOMENDACION: se muestra de la más a la menos favorable.
const OPCIONES_RECOMENDACION: Array<{ valor: number; etiqueta: string }> = [
  { valor: 5, etiqueta: "Sí, definitivamente" },
  { valor: 4, etiqueta: "Probablemente sí" },
  { valor: 3, etiqueta: "Tal vez" },
  { valor: 2, etiqueta: "Probablemente no" },
  { valor: 1, etiqueta: "No" },
];

const COMENTARIO_DEFAULT =
  "¿Qué aspecto de tu selección, inducción o adaptación al puesto considerás que debemos mejorar?";

const CONFIDENCIALIDAD_DEFAULT =
  "Tus respuestas serán confidenciales y utilizadas únicamente por Gestión de Talento para mejorar el proceso de selección, inducción y acompañamiento de los nuevos colaboradores.";

const LARGO_MAX_TEXTO = 1000;

// Momentos de seguimiento por antigüedad (D30, D60, D90): se muestra la etiqueta.
const ES_MOMENTO_SEGUIMIENTO = /^D\d+$/;

interface Props {
  isAuth: boolean;
}

const claseOpcion = (seleccionada: boolean) =>
  seleccionada ? "me-likert-option me-likert-option--selected" : "me-likert-option";

const MicroEncuestaModal: React.FC<Props> = ({ isAuth }) => {
  const [present] = useIonToast();

  const [abierto, setAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [encuesta, setEncuesta] = useState<MicroEncuesta | null>(null);
  // Respuestas: { [preguntaId]: valor } y textos (TEXTO u opción "Otro").
  const [respuestas, setRespuestas] = useState<Record<number, number>>({});
  const [textos, setTextos] = useState<Record<number, string>>({});
  const [comentario, setComentario] = useState("");
  // Abierta desde la tarjeta del inicio (no la abrió la app sola).
  const [voluntaria, setVoluntaria] = useState(false);

  const yaVerificado = useRef(false);

  const abrir = (nueva: MicroEncuesta, esVoluntaria: boolean) => {
    if (!(nueva.preguntas || []).length) return;
    setEncuesta(nueva);
    setRespuestas({});
    setTextos({});
    setComentario("");
    setVoluntaria(esVoluntaria);
    setAbierto(true);
  };

  // Verificación inicial al autenticarse (una sola vez por sesión).
  useEffect(() => {
    if (!isAuth) {
      yaVerificado.current = false;
      setAbierto(false);
      setEncuesta(null);
      return;
    }
    if (yaVerificado.current) return;
    yaVerificado.current = true;
    (async () => {
      const estado = await MicroEncuestaAPI.estado();
      if (estado.abrir && estado.encuesta) abrir(estado.encuesta, false);
    })();
  }, [isAuth]);

  // Apertura desde la tarjeta del inicio.
  useEffect(() => {
    const onAbrir = (e: Event) => {
      const nueva = (e as CustomEvent<MicroEncuesta>).detail;
      if (nueva) abrir(nueva, true);
    };
    window.addEventListener(EVENTO_MICROENCUESTA_ABRIR, onAbrir);
    return () => window.removeEventListener(EVENTO_MICROENCUESTA_ABRIR, onAbrir);
  }, []);

  const setValor = (preguntaId: number, valor: number) =>
    setRespuestas((prev) => ({ ...prev, [preguntaId]: valor }));

  const setTexto = (preguntaId: number, texto: string) =>
    setTextos((prev) => ({ ...prev, [preguntaId]: texto }));

  const postergacion = encuesta?.postergacion;
  // Abierta desde la tarjeta no cuenta como postergación: "Más tarde" solo cierra.
  const postergacionControlada = postergacion?.maximo != null && !voluntaria;
  // Sin control (nuevos ingresos / desde la tarjeta) siempre se puede cerrar;
  // con control, mientras queden postergaciones.
  const puedePosponer = !postergacionControlada || !!postergacion?.permitida;
  const tienePreguntaTexto = (encuesta?.preguntas || []).some((p) => p.tipo === "TEXTO");

  const opcionElegida = (p: MicroEncuestaPregunta) =>
    p.opciones.find((o) => o.valor === respuestas[p.id]);

  /** True si la pregunta tiene una respuesta válida para su tipo. */
  const respondida = (p: MicroEncuestaPregunta) => {
    if (p.tipo === "TEXTO") return !!(textos[p.id] || "").trim();
    if (respuestas[p.id] === undefined) return false;
    if (p.tipo === "OPCION") {
      const opcion = opcionElegida(p);
      return !!opcion && (!opcion.requiere_texto || !!(textos[p.id] || "").trim());
    }
    return true;
  };

  const handleMasTarde = async () => {
    if (!encuesta) return;
    if (!postergacionControlada) {
      setAbierto(false);
      return;
    }
    setGuardando(true);
    try {
      const res = await MicroEncuestaAPI.postergar(encuesta.id);
      const restantes = res?.restantes ?? 0;
      present({
        message:
          restantes > 0
            ? `Te la volvemos a mostrar mañana. Podés posponerla ${restantes} ${restantes === 1 ? "vez" : "veces"} más.`
            : "Te la volvemos a mostrar mañana. La próxima vez vas a tener que responderla.",
        duration: 4000,
        color: "medium",
      });
      setAbierto(false);
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "No se pudo posponer la encuesta",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setGuardando(false);
    }
  };

  const handleEnviar = async () => {
    if (!encuesta) return;
    const faltan = encuesta.preguntas.filter((p) => p.obligatoria !== false && !respondida(p));
    if (faltan.length > 0) {
      const otroSinTexto = faltan.find((p) => p.tipo === "OPCION" && opcionElegida(p)?.requiere_texto);
      present({
        message: otroSinTexto
          ? `Contanos cuál es tu opción "${opcionElegida(otroSinTexto)?.texto}"`
          : "Debés responder todas las preguntas",
        duration: 3000,
        color: "warning",
      });
      return;
    }

    const payload: MicroEncuestaRespuesta[] = encuesta.preguntas
      .filter((p) => respondida(p))
      .map((p) => {
        const texto = (textos[p.id] || "").trim();
        if (p.tipo === "TEXTO") return { pregunta_id: p.id, texto };
        if (p.tipo === "OPCION" && opcionElegida(p)?.requiere_texto)
          return { pregunta_id: p.id, valor: respuestas[p.id], texto };
        return { pregunta_id: p.id, valor: respuestas[p.id] };
      });

    setGuardando(true);
    try {
      await MicroEncuestaAPI.enviar(
        encuesta.id,
        payload,
        tienePreguntaTexto ? undefined : comentario.trim() || undefined
      );
      present({ message: "¡Gracias por tu opinión!", duration: 3000, color: "success" });
      setAbierto(false);
      window.dispatchEvent(new Event(EVENTO_MICROENCUESTA_RESPONDIDA));
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "No se pudo guardar la encuesta",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setGuardando(false);
    }
  };

  const renderRespuesta = (p: MicroEncuestaPregunta) => {
    if (p.tipo === "RECOMENDACION") {
      return (
        <div className="me-recomendacion" role="radiogroup" aria-label={p.texto}>
          {OPCIONES_RECOMENDACION.map((op) => (
            <button
              key={op.valor}
              type="button"
              role="radio"
              aria-checked={respuestas[p.id] === op.valor}
              className={claseOpcion(respuestas[p.id] === op.valor)}
              onClick={() => setValor(p.id, op.valor)}
            >
              {op.etiqueta}
            </button>
          ))}
        </div>
      );
    }

    if (p.tipo === "OPCION") {
      const elegida = opcionElegida(p);
      return (
        <>
          <div className="me-recomendacion" role="radiogroup" aria-label={p.texto}>
            {p.opciones.map((op) => (
              <button
                key={op.valor}
                type="button"
                role="radio"
                aria-checked={respuestas[p.id] === op.valor}
                className={claseOpcion(respuestas[p.id] === op.valor)}
                onClick={() => setValor(p.id, op.valor)}
              >
                {op.texto}
              </button>
            ))}
          </div>
          {elegida?.requiere_texto && (
            <textarea
              className="me-comentario-textarea me-texto-opcion"
              rows={2}
              maxLength={LARGO_MAX_TEXTO}
              placeholder="Contanos cuál..."
              value={textos[p.id] || ""}
              onChange={(e) => setTexto(p.id, e.target.value)}
            />
          )}
        </>
      );
    }

    if (p.tipo === "NPS") {
      return (
        <>
          <div className="me-nps" role="radiogroup" aria-label="Escala 0 a 10">
            {OPCIONES_NPS.map((op) => (
              <button
                key={op}
                type="button"
                role="radio"
                aria-checked={respuestas[p.id] === op}
                className={claseOpcion(respuestas[p.id] === op)}
                onClick={() => setValor(p.id, op)}
              >
                {op}
              </button>
            ))}
          </div>
          <div className="me-nps-leyenda">
            <span>0 = Nada probable</span>
            <span>10 = Totalmente probable</span>
          </div>
        </>
      );
    }

    if (p.tipo === "TEXTO") {
      return (
        <textarea
          className="me-comentario-textarea"
          rows={4}
          maxLength={LARGO_MAX_TEXTO}
          placeholder="Escribí tu respuesta..."
          value={textos[p.id] || ""}
          onChange={(e) => setTexto(p.id, e.target.value)}
        />
      );
    }

    return (
      <div className="me-likert" role="radiogroup" aria-label="Escala 1 a 5">
        {OPCIONES.map((op) => (
          <button
            key={op}
            type="button"
            role="radio"
            aria-checked={respuestas[p.id] === op}
            className={claseOpcion(respuestas[p.id] === op)}
            onClick={() => setValor(p.id, op)}
          >
            {op}
          </button>
        ))}
      </div>
    );
  };

  return (
    <IonModal isOpen={abierto} backdropDismiss={false}>
      <IonHeader>
        <IonToolbar>
          <IonTitle style={{ fontSize: 16 }}>{encuesta?.titulo || "Microencuesta"}</IonTitle>
          {puedePosponer && (
            <IonButtons slot="end">
              <IonButton onClick={handleMasTarde} disabled={guardando}>
                Más tarde
              </IonButton>
            </IonButtons>
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div style={{ padding: 16 }}>
          {encuesta?.subtitulo && <p className="me-subtitulo">{encuesta.subtitulo}</p>}
          {encuesta?.momento && ES_MOMENTO_SEGUIMIENTO.test(encuesta.momento.codigo) && (
            <div className="me-momento">Seguimiento de tus primeros {encuesta.momento.nombre}</div>
          )}
          {postergacionControlada && (
            <div className="me-postergacion">
              {puedePosponer
                ? `Podés responderla más tarde ${postergacion?.restantes} ${postergacion?.restantes === 1 ? "vez" : "veces"} más.`
                : "Esta vez es necesario que la respondas. ¡Gracias por tu tiempo!"}
            </div>
          )}

          <div className="me-escala">
            <div className="me-escala-titulo">
              Marcá la opción que mejor represente tu experiencia:
            </div>
            {OPCIONES.map((op) => (
              <div key={op}>
                {op} = {ETIQUETAS_ESCALA[op]}
              </div>
            ))}
          </div>

          {(encuesta?.preguntas || []).map((p, idx) => (
            <div key={p.id} className="me-pregunta">
              {p.eje && <div className="me-pregunta-eje">{p.eje}</div>}
              <div className="me-pregunta-texto">
                {idx + 1}. {p.texto}
                {p.obligatoria === false && (
                  <span style={{ fontWeight: 400 }}> (opcional)</span>
                )}
              </div>
              {renderRespuesta(p)}
            </div>
          ))}

          {!tienePreguntaTexto && (
            <>
              <div className="me-comentario-label">
                {encuesta?.pregunta_comentario || COMENTARIO_DEFAULT}{" "}
                <span style={{ fontWeight: 400 }}>(opcional)</span>
              </div>
              <textarea
                className="me-comentario-textarea"
                rows={4}
                placeholder="Escribí tu comentario..."
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
              />
            </>
          )}

          <div className="me-confidencialidad">
            {encuesta?.texto_confidencialidad || CONFIDENCIALIDAD_DEFAULT}
          </div>

          <IonButton expand="block" onClick={handleEnviar} disabled={guardando}>
            {guardando ? "Guardando..." : "Enviar encuesta"}
          </IonButton>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default MicroEncuestaModal;
